import { Injectable } from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import { switchMap, tap, timeout, catchError } from 'rxjs/operators';
import { ApiService } from '@services/micro/api.service';
import { NewRelicEventsService } from '@services/new-relic-events.service';
import { Reservation } from '@models/micro/reservation.model';
import { ViewDocuments } from '@models/micro/response/viewDocuments.model';
import { PAX_TYPE } from '@app/enums';
import { NEW_RELIC_EVENT } from '@app/enums/new-relic.enum';

@Injectable({
  providedIn: 'root',
})
export class GetViewDocumentsRetryService {
  private readonly RETRY_DELAY_MS = 1000;
  private readonly MAX_RETRIES = 1;

  constructor(
    private _apiService: ApiService,
    private _newRelicEventsService: NewRelicEventsService
  ) {}

  /**
   * Calls getViewDocuments with automatic retry when passengerIds are missing.
   * Returns the final response (from first attempt or retry).
   */
  getViewDocumentsWithRetry(reservation: Reservation): Observable<any> {
    return this._apiService.getRequireDocuments(reservation).pipe(
      switchMap((firstResponse) => {
        const viewDocs = new ViewDocuments(firstResponse);
        if (!this.needsRetry(viewDocs, reservation)) {
          return of(firstResponse);
        }
        this.logRetryAttempted(reservation, viewDocs);
        return timer(this.RETRY_DELAY_MS).pipe(
          switchMap(() => this._apiService.getRequireDocuments(reservation)),
          timeout(30000),
          catchError(() => of(firstResponse)),
          tap((retryResponse) => this.logRetryOutcome(retryResponse, reservation))
        );
      })
    );
  }

  /**
   * Determines if a retry is needed based on the response passengers.
   * Returns true when any non-infant passenger without passengerIdSkyteam
   * has empty/absent passengerIds or passengerIds with no valid id.
   */
  needsRetry(response: ViewDocuments, reservation: Reservation): boolean {
    if (!response?.passengers || !reservation?.passengers) {
      return false;
    }

    return reservation.passengers
      .filter((pax) => pax.paxType !== PAX_TYPE.INFANT)
      .some((pax) => {
        if (pax.passengerIdSkyteam) {
          return false;
        }
        const docPassenger = response.passengers.find(
          (dp) => dp.passengerNameNumber === pax.nameRefNumber
        );
        if (!docPassenger) {
          return true;
        }
        if (docPassenger.passengerIdSkyteam) {
          return false;
        }
        const hasValidIds =
          Array.isArray(docPassenger.passengerIds) &&
          docPassenger.passengerIds.length > 0 &&
          docPassenger.passengerIds.some((entry) => !!entry?.id);
        return !hasValidIds;
      });
  }

  /**
   * Logs the retry-attempted event to New Relic.
   */
  private logRetryAttempted(reservation: Reservation, viewDocs: ViewDocuments): void {
    const missingPaxNumbers = this.getMissingPaxNumbers(viewDocs, reservation);
    this._newRelicEventsService.trackEvent(NEW_RELIC_EVENT.KIOSK_PASSENGER_ID_RETRY_ATTEMPTED, {
      reservationCode: reservation.pnr,
      missingCount: missingPaxNumbers.length,
      missingPaxNumbers: missingPaxNumbers.join(','),
    });
  }

  /**
   * Evaluates the retry response and emits either a success or failure telemetry event.
   * Success: all previously-missing passengers now have passengerIds.
   * Failure: one or more passengers still lack passengerIds.
   */
  private logRetryOutcome(retryResponse: any, reservation: Reservation): void {
    const retryViewDocs = new ViewDocuments(retryResponse);
    const stillMissingPaxNumbers = this.getMissingPaxNumbers(retryViewDocs, reservation);

    if (stillMissingPaxNumbers.length === 0) {
      const recoveredCount = reservation.passengers.filter(
        (pax) => pax.paxType !== PAX_TYPE.INFANT && !pax.passengerIdSkyteam
      ).length;
      this._newRelicEventsService.trackEvent(NEW_RELIC_EVENT.KIOSK_PASSENGER_ID_RETRY_SUCCESS, {
        reservationCode: reservation.pnr,
        recoveredCount,
      });
    } else {
      this._newRelicEventsService.trackEvent(NEW_RELIC_EVENT.KIOSK_PASSENGER_ID_RETRY_FAILURE, {
        reservationCode: reservation.pnr,
        stillMissingPaxNumbers: stillMissingPaxNumbers.join(','),
      });
    }
  }

  /**
   * Returns the passengerNameNumber values for non-infant passengers
   * that are missing valid passengerIds in the response.
   */
  private getMissingPaxNumbers(viewDocs: ViewDocuments, reservation: Reservation): string[] {
    return reservation.passengers
      .filter((pax) => pax.paxType !== PAX_TYPE.INFANT)
      .filter((pax) => {
        if (pax.passengerIdSkyteam) {
          return false;
        }
        const docPassenger = viewDocs.passengers.find(
          (dp) => dp.passengerNameNumber === pax.nameRefNumber
        );
        if (!docPassenger) {
          return true;
        }
        if (docPassenger.passengerIdSkyteam) {
          return false;
        }
        const hasValidIds =
          Array.isArray(docPassenger.passengerIds) &&
          docPassenger.passengerIds.length > 0 &&
          docPassenger.passengerIds.some((entry) => !!entry?.id);
        return !hasValidIds;
      })
      .map((pax) => pax.nameRefNumber);
  }
}
